<template>
  <div class="jt">
    <template
      v-for="(route, index) in routes"
      :key="route.uid"
    >
      <div class="jt-leg">
        <!-- Timeline rail -->
        <div class="jt-rail">
          <span class="jt-node">{{ index + 1 }}</span>
          <span
            v-if="index < routes.length - 1"
            class="jt-line"
          />
        </div>

        <!-- Corsa card -->
        <div
          class="jt-card"
          :class="{
            'jt-card--overlap': route.availability.state === 'unavailable',
            'is-complete': isLegComplete(route),
          }"
        >
          <p class="jt-corsa">
            <span
              v-if="isLegComplete(route)"
              class="jt-check"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </span>
            Corsa {{ index + 1 }}
            <span
              v-if="isLegComplete(route)"
              class="jt-ready"
            >Pronta</span>
          </p>

          <!-- Disponibilità verificata dal backend: capacità della provincia di
               partenza e driver liberi, contando anche le corse di altre sedi. -->
          <div
            v-if="route.availability.state === 'unavailable'"
            class="jt-overlap"
            role="alert"
          >
            ⚠ Non disponibile: {{ availabilityMessage(route) }}
            <button
              v-if="route.availability.firstStart && !locked"
              type="button"
              class="jt-avail-use"
              @click="useFirstStart(index)"
            >
              Usa {{ route.availability.firstStart }}
            </button>
          </div>
          <div
            v-else-if="route.availability.state === 'loading'"
            class="jt-avail"
            aria-live="polite"
          >
            Verifica della disponibilità…
          </div>
          <div
            v-else-if="route.availability.state === 'ok'"
            class="jt-avail jt-avail--ok"
            aria-live="polite"
          >
            Disponibile
          </div>
          <div
            v-else-if="route.availability.state === 'error'"
            class="jt-avail"
          >
            Disponibilità non verificata: la controlla il salvataggio.
          </div>
          <div class="jt-head">
            <div
              class="jt-segment"
              role="group"
              aria-label="Tipo spostamento"
            >
              <button
                type="button"
                class="jt-seg-btn"
                :class="{ 'is-active': route.isShuttle }"
                :aria-pressed="route.isShuttle ? 'true' : 'false'"
                :disabled="locked || !!soloCatena(index)"
                :title="soloCatena(index) || undefined"
                @click="setShuttle(index, true)"
              >
                Spostamento singolo
              </button>
              <button
                type="button"
                class="jt-seg-btn"
                :class="{ 'is-active': !route.isShuttle }"
                :aria-pressed="!route.isShuttle ? 'true' : 'false'"
                :disabled="locked"
                @click="setShuttle(index, false)"
              >
                Spostamento a catena
              </button>
            </div>
            <!-- Come funziona «a catena»: per esteso solo a richiesta. -->
            <el-tooltip
              v-if="notaCorsa(index)?.info"
              :content="notaCorsa(index).info"
              placement="top"
              effect="light"
            >
              <span
                class="jt-info"
                tabindex="0"
                role="img"
                :aria-label="notaCorsa(index).info"
              >i</span>
            </el-tooltip>

            <button
              v-if="canRemove(index)"
              type="button"
              class="jt-del"
              :aria-label="`Elimina corsa ${index + 1}`"
              title="Elimina corsa"
              @click="confirmRemove(index)"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
              </svg>
            </button>
          </div>

          <!-- «A catena»: cosa succede al driver. Il caso normale sta nella «i»
               accanto al tipo di spostamento; qui si scrive solo quello che serve
               sapere (catena chiusa, a bordo di un'altra corsa) o fare (scegliere
               come rientra il driver). -->
          <div
            v-if="notaCorsa(index)?.testo || rientroDaScegliere(route)"
            class="jt-chain-note"
            :class="`jt-chain-note--${notaCorsa(index)?.tipo || 'info'}`"
          >
            <span v-if="notaCorsa(index)?.testo">{{ notaCorsa(index).testo }}</span>
            <template v-if="notaCorsa(index)?.tipo === 'scoperto' && !locked">
              <button
                type="button"
                class="jt-chain-btn"
                @click="aggiungiRientro(index)"
              >
                Aggiungi il rientro
              </button>
              <button
                type="button"
                class="jt-chain-btn"
                @click="scegliComeRientra(index)"
              >
                Rientra a bordo di un'altra corsa
              </button>
            </template>
            <button
              v-else-if="route.rientroAltrove && route.isShuttle === false && !locked"
              type="button"
              class="jt-chain-btn"
              @click="aggiungiRientro(index)"
            >
              Aggiungi invece il rientro
            </button>
          </div>
          <!-- C'è già una corsa con cui il driver può tornare (a bordo di
               un'altra catena o col jolly di un singolo): la si può usare al
               posto del rientro proposto. -->
          <div
            v-else-if="!locked && puoRientrareAltrove(index)"
            class="jt-chain-note jt-chain-note--info"
          >
            <span>Invece del rientro proposto il driver può tornare con una corsa già prevista.</span>
            <button
              type="button"
              class="jt-chain-btn"
              @click="scegliComeRientra(index)"
            >
              Rientra con un'altra corsa
            </button>
          </div>

          <!-- CON QUALE CORSA RIENTRA: una scheda per ogni corsa che può
               riportarlo (a catena, parte da lì dopo il suo arrivo, lo riporta da
               dove era partito, ha un posto), anche di un altro spostamento del
               giorno. Il passeggero di quella corsa è il driver di questa. -->
          <fieldset
            v-if="route.rientroAltrove && route.isShuttle === false && !locked"
            class="jt-rientro"
            :class="{ 'jt-rientro--todo': rientroDaScegliere(route) }"
          >
            <legend class="jt-rientro__q">
              Con quale corsa rientra il driver da {{ nomeArrivo(route) }}?
            </legend>
            <div class="jt-rientro__opts">
              <button
                v-for="o in opzioniRientro(index)"
                :key="o.value"
                type="button"
                class="jt-rientro__opt"
                :class="{ 'is-active': route.rientroSu === o.value }"
                :aria-pressed="route.rientroSu === o.value ? 'true' : 'false'"
                @click="scegliRientro(index, o.value)"
              >
                <span class="jt-rientro__t">{{ o.jolly ? 'Con il jolly della' : 'A bordo della' }} {{ o.titolo }}</span>
                <span class="jt-rientro__d">{{ o.dettaglio }}</span>
              </button>
            </div>
            <p
              v-if="!opzioniRientro(index).length"
              class="jt-rientro__none"
            >
              Nessuna corsa lo può riportare: aggiungi una corsa a catena che parta da
              {{ nomeArrivo(route) }} dopo le {{ route.dateEnd || '…' }} e lo riporti da dove è partito,
              oppure rendi questo spostamento singolo (con il jolly).
            </p>
          </fieldset>

          <!-- Griglia: Sede e Orario appaiati (Partenza sopra, Arrivo sotto). -->
          <div class="jt-grid">
            <div class="jt-cell">
              <span class="jt-label">Partenza</span>
              <el-autocomplete
                v-if="!route.autoChained"
                v-model="route.startText"
                :fetch-suggestions="querySuggestions"
                placeholder="Sede o indirizzo"
                class="jt-input"
                :disabled="locked"
                @select="(item) => onStartSelect(item.value, index)"
                @change="(val) => onStartChange(val, index)"
              />
              <span
                v-if="!route.autoChained && route.startResolved"
                class="jt-geo"
              >
                <span class="jt-geo__label">Indirizzo geolocalizzato</span>
                {{ route.startResolved }}
              </span>
              <div
                v-if="route.autoChained"
                class="jt-locked"
                title="Partenza vincolata alla corsa precedente"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <rect
                    x="4"
                    y="11"
                    width="16"
                    height="10"
                    rx="2"
                  />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
                {{ route.startText || '—' }}
              </div>
            </div>
            <div class="jt-cell">
              <span class="jt-label">Orario partenza</span>
              <CampoOrario
                v-model="route.dateStart"
                :min="minimoPartenza(index)"
                :disabled="locked"
                etichetta="Orario partenza"
                @change="() => calculateArrivalTime(route, index)"
              />
              <!-- Fra una corsa e la successiva almeno 5 minuti di stacco (come
                   nel conteggio della disponibilità); se il trasferimento è più
                   lungo, vale quello. -->
              <span
                v-if="index > 0 && route.transitMin > 5"
                class="jt-transit"
              >
                + {{ route.transitMin }} min di trasferimento dall'arrivo
                precedente
              </span>
              <span
                v-else-if="index > 0"
                class="jt-transit"
              >
                + 5 min di stacco dall'arrivo della corsa precedente
              </span>
            </div>

            <div class="jt-cell">
              <span class="jt-label">Arrivo</span>
              <el-autocomplete
                v-model="route.endText"
                :fetch-suggestions="querySuggestions"
                placeholder="Sede o indirizzo"
                class="jt-input"
                :disabled="locked"
                @select="(item) => onEndSelect(item.value, index)"
                @change="(val) => onEndChange(val, index)"
              />
              <span
                v-if="route.endResolved"
                class="jt-geo"
              >
                <span class="jt-geo__label">Indirizzo geolocalizzato</span>
                {{ route.endResolved }}
              </span>
            </div>
            <div class="jt-cell">
              <span class="jt-label">Orario arrivo</span>
              <CampoOrario
                :model-value="route.dateEnd"
                disabled
                etichetta="Orario arrivo"
              />
              <span
                v-if="oltreMezzanotte(route)"
                class="jt-transit jt-transit--err"
              >
                Arriverebbe dopo mezzanotte: anticipa la partenza
              </span>
            </div>
          </div>

          <div class="jt-preview">
            <span
              v-if="route.previewLoading"
              class="jt-badge jt-badge--load"
            >
              calcolo…
            </span>
            <!-- Perché km e minuti non ci sono: lo dice qui, sulla corsa. -->
            <span
              v-else-if="route.previewError"
              class="jt-badge jt-badge--attn"
            >{{ route.previewError }}</span>
            <template v-else-if="route.previewKm != null">
              <span class="jt-badge">{{ route.previewKm.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} km</span>
              <span class="jt-badge">{{ Math.round(route.previewMin) }} min</span>
            </template>
          </div>

          <!-- Veicolo. Con più auto sulla stessa corsa, questa è l'auto 1. -->
          <div
            v-if="route.altreAuto.length"
            class="jt-auto-head"
          >
            <span class="jt-auto-n">Auto 1</span>
          </div>
          <div class="jt-vehicle">
            <label class="jt-field">
              <span class="jt-label">Targa</span>
              <input
                v-model="route.targa"
                class="jt-underline"
                :placeholder="route.nonImmatricolato ? 'Facoltativa' : '—'"
                :disabled="locked"
              >
            </label>
            <label class="jt-field">
              <span class="jt-label">Marca</span>
              <input
                v-model="route.marca"
                class="jt-underline"
                placeholder="—"
                :disabled="locked"
              >
            </label>
            <label class="jt-field">
              <span class="jt-label">Modello</span>
              <input
                v-model="route.modello"
                class="jt-underline"
                placeholder="—"
                :disabled="locked"
              >
            </label>
            <label class="jt-field">
              <span class="jt-label">Dettagli</span>
              <input
                v-model="route.details"
                class="jt-underline"
                placeholder="Note"
                :disabled="locked"
              >
            </label>
            <!-- Il cambio chiude la riga dei dati del veicolo: è una
                 caratteristica del mezzo come targa e modello. La colonna è
                 `auto` perché i suoi due pulsanti devono stare affiancati. -->
            <div class="jt-field jt-field--cambio">
              <span class="jt-label">Tipo di cambio</span>
              <el-radio-group
                v-model="route.gearbox_type"
                size="small"
                :disabled="locked"
                class="jt-gearbox"
              >
                <el-radio-button label="MANUAL">
                  Manuale
                </el-radio-button>
                <el-radio-button label="AUTOMATIC">
                  Automatico
                </el-radio-button>
              </el-radio-group>
            </div>
            <!-- Un veicolo NON immatricolato si sposta con la targa prova: il
                 driver deve prenderla e riportarla, e la tariffa cambia. È
                 l'eccezione, quindi la casella nasce spenta. Sta su una riga
                 sua: è una domanda, non un campo da compilare. -->
            <div class="jt-vehicle__opts">
              <el-checkbox
                v-model="route.nonImmatricolato"
                :disabled="locked || !targaProvaLibera(route, route)"
                class="jt-imm"
              >
                Veicolo <b>non</b> immatricolato (targa prova)
              </el-checkbox>
              <span
                v-if="!locked && !targaProvaLibera(route, route)"
                class="jt-imm-note"
              >
                Le targhe prova sono {{ MAX_TARGHE_PROVA }}: a quest'ora sono già tutte in questo spostamento
              </span>
              <span
                v-else-if="route.nonImmatricolato && route.provaNonDisponibile"
                class="jt-imm-note jt-imm-note--err"
              >
                Nessuna targa prova libera a quest'ora: sono già in uso tutte e {{ MAX_TARGHE_PROVA }}
              </span>
              <!-- TARIFFA della corsa (fasce del partner per km, tipo e targa
                   prova) e il PREZZO deciso a mano, che la sostituisce.
                   Vuoto = vale la tariffa. -->
              <div class="jt-prezzo">
                <span class="jt-prezzo__k">Tariffa</span>
                <span
                  class="jt-prezzo__v"
                  :class="{ 'is-off': leggiPrezzo(route.prezzoManuale) !== null }"
                >{{ testoTariffa(route) }}</span>
                <el-input
                  v-model="route.prezzoManuale"
                  size="small"
                  class="jt-prezzo__in"
                  placeholder="a mano"
                  :disabled="locked"
                  :aria-label="`Prezzo a mano della corsa ${index + 1}`"
                >
                  <template #prepend>
                    €
                  </template>
                </el-input>
              </div>
            </div>
          </div>
          <!-- AUTO IN PIÙ: stessa partenza, arrivo e orario, fino a 5 in
               tutto. Ogni auto ha il suo driver; al salvataggio diventa una
               corsa sua (a catena i loro driver rientrano a bordo con il
               driver della prima auto). -->
          <template
            v-for="(auto, k) in route.altreAuto"
            :key="auto.uid"
          >
            <div class="jt-auto-head">
              <span class="jt-auto-n">Auto {{ k + 2 }}</span>
              <button
                v-if="!locked"
                type="button"
                class="jt-auto-del"
                :aria-label="`Togli l'auto ${k + 2}`"
                @click="confermaTogliAuto(index, k)"
              >
                Togli
              </button>
            </div>
            <div class="jt-vehicle jt-vehicle--extra">
              <label class="jt-field">
                <span class="jt-label">Targa</span>
                <input
                  v-model="auto.targa"
                  class="jt-underline"
                  :placeholder="auto.nonImmatricolato ? 'Facoltativa' : '—'"
                  :disabled="locked"
                >
              </label>
              <label class="jt-field">
                <span class="jt-label">Marca</span>
                <input
                  v-model="auto.marca"
                  class="jt-underline"
                  placeholder="—"
                  :disabled="locked"
                >
              </label>
              <label class="jt-field">
                <span class="jt-label">Modello</span>
                <input
                  v-model="auto.modello"
                  class="jt-underline"
                  placeholder="—"
                  :disabled="locked"
                >
              </label>
              <label class="jt-field">
                <span class="jt-label">Dettagli</span>
                <input
                  v-model="auto.details"
                  class="jt-underline"
                  placeholder="Note"
                  :disabled="locked"
                >
              </label>
              <div class="jt-field jt-field--cambio">
                <span class="jt-label">
                  Tipo di cambio
                  <span class="jt-optional">facoltativo</span>
                </span>
                <el-radio-group
                  v-model="auto.gearbox_type"
                  size="small"
                  :disabled="locked"
                  class="jt-gearbox"
                >
                  <el-radio-button label="MANUAL">
                    Manuale
                  </el-radio-button>
                  <el-radio-button label="AUTOMATIC">
                    Automatico
                  </el-radio-button>
                </el-radio-group>
              </div>
              <div class="jt-vehicle__opts">
                <el-checkbox
                  v-model="auto.nonImmatricolato"
                  :disabled="locked || !targaProvaLibera(route, auto)"
                  class="jt-imm"
                >
                  Veicolo <b>non</b> immatricolato (targa prova)
                </el-checkbox>
                <span
                  v-if="!locked && !targaProvaLibera(route, auto)"
                  class="jt-imm-note"
                >
                  Le targhe prova sono {{ MAX_TARGHE_PROVA }}: a quest'ora sono già tutte in questo spostamento
                </span>
                <span
                  v-else-if="auto.nonImmatricolato && auto.provaNonDisponibile"
                  class="jt-imm-note jt-imm-note--err"
                >
                  Nessuna targa prova libera a quest'ora: sono già in uso tutte e {{ MAX_TARGHE_PROVA }}
                </span>
                <!-- TARIFFA della corsa (fasce del partner per km, tipo e targa
                     prova) e il PREZZO deciso a mano, che la sostituisce.
                     Vuoto = vale la tariffa. -->
                <div class="jt-prezzo">
                  <span class="jt-prezzo__k">Tariffa</span>
                  <span
                    class="jt-prezzo__v"
                    :class="{ 'is-off': leggiPrezzo(auto.prezzoManuale) !== null }"
                  >{{ testoTariffa(auto) }}</span>
                  <el-input
                    v-model="auto.prezzoManuale"
                    size="small"
                    class="jt-prezzo__in"
                    placeholder="a mano"
                    :disabled="locked"
                    :aria-label="`Prezzo a mano dell'auto ${k + 2}`"
                  >
                    <template #prepend>
                      €
                    </template>
                  </el-input>
                </div>
              </div>
            </div>
          </template>
          <p
            v-if="notaAuto(index)"
            class="jt-auto-note"
          >
            {{ notaAuto(index) }}
          </p>
          <button
            v-if="!locked && puoAggiungereAuto(index)"
            type="button"
            class="jt-auto-add"
            @click="aggiungiAuto(index)"
          >
            + Aggiungi un'altra auto con la stessa partenza e lo stesso orario
            <span class="jt-optional">fino a {{ MAX_AUTO }} auto</span>
          </button>
        </div>
      </div>

      <!-- Inserimento IN-LINE tra due corse esistenti (disattivato se locked) -->
      <div
        v-if="canInsertAfter(index) && !locked"
        class="jt-insert-row"
      >
        <button
          type="button"
          class="jt-insert"
          @click="insertRouteAfter(index)"
        >
          + Inserisci corsa qui
        </button>
      </div>
    </template>

    <button
      v-if="!locked"
      type="button"
      class="jt-add-btn"
      @click="addRouteAtEnd()"
    >
      <span class="jt-add-plus">+</span> Aggiungi Corsa
    </button>

    <!-- Banner quando l'edit-lock è scattato: solo eliminazione consentita -->
    <div
      v-if="locked"
      class="jt-lockbanner"
    >
      🔒 Modifiche bloccate (oltre le 15:00 del giorno precedente). Puoi solo
      <strong>eliminare</strong> le corse; l'eliminazione verrà inviata
      all'amministrazione per la decisione di contabilizzazione.
    </div>

    <div
      v-if="routes.length > 0"
      class="jt-summary"
    >
      <span class="jt-summary-k">Totale</span>
      <span class="jt-summary-v">{{ totalPreviewKm.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} km</span>
      <span class="jt-summary-dot">·</span>
      <span class="jt-summary-v">{{ totalPreviewMin }} min</span>
      <!-- Il prezzo dello spostamento: tariffe e prezzi a mano, senza IVA.
           Solo se ogni auto ha il suo, altrimenti sembrerebbe il totale. -->
      <template v-if="totalePrezzo !== null">
        <span class="jt-summary-dot">·</span>
        <span class="jt-summary-v">{{ euro(totalePrezzo) }}</span>
        <span class="jt-summary-k">+ IVA</span>
      </template>
    </div>
    <!-- Le azioni di chi ospita l'editor (es. «Salva»), sulla stessa riga,
         a destra del totale. -->
    <div
      v-if="$slots.azioni"
      class="jt-azioni"
    >
      <slot name="azioni" />
    </div>

    <!-- La giornata: le sedi e gli spostamenti delle auto fra di loro. -->
    <MappaSpostamenti
      :mappa="mappa"
      class="jt-giri"
    />

    <!-- Alert sforamento a 3 scelte (Procedi / Inserisci dopo / Annulla) -->
    <el-dialog
      v-model="overlapVisible"
      title="Sovrapposizione orari"
      width="480px"
      :close-on-click-modal="false"
      @closed="onOverlapClosed"
    >
      <p class="jt-overlap-msg">
        Le tempistiche di questa corsa si sovrappongono alle successive. Come
        vuoi procedere?
      </p>
      <template #footer>
        <div class="jt-overlap-actions">
          <button
            type="button"
            class="jt-ov-btn jt-ov-btn--primary"
            @click="chooseOverlap('shift')"
          >
            Procedi (sposta le successive)
          </button>
          <button
            type="button"
            class="jt-ov-btn jt-ov-btn--alt"
            @click="chooseOverlap('after')"
          >
            Inserisci dopo
          </button>
          <button
            type="button"
            class="jt-ov-btn jt-ov-btn--ghost"
            @click="chooseOverlap('cancel')"
          >
            Annulla
          </button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { toRef, onMounted, ref } from 'vue';
import { ElMessageBox } from 'element-plus';
import { useJourneyLegs } from '@/composables/useJourneyLegs';
import '@/styles/journey-timeline.css';
import MappaSpostamenti from './MappaSpostamenti.vue';
import CampoOrario from './CampoOrario.vue';

// ---------------------------------------------------------------------------
//  PORTATO DAL GESTIONALE PARTNER — `src/Components/JourneyLegsEditor.vue`.
//
//  Il markup è quello, invariato: regole e comportamenti sono già validati e
//  vanno preservati. L'unica aggiunta è la prop `headquarters`, l'elenco delle
//  sedi del partner scelto, che nel gestionale partner era implicito
//  nell'utente loggato. Il foglio di stile ha le stesse classi ma è riscritto
//  sui token di questo gestionale (vedi `styles/journey-timeline.css`).
// ---------------------------------------------------------------------------
export default {
  name: 'JourneyLegsEditor',
  components: { MappaSpostamenti, CampoOrario },
  props: {
    // Corse iniziali (edit mode). Forma "editor": startPoint/endPoint oggetti,
    // startText/endText, dateStart/dateEnd "HH:mm", marca/modello/targa,
    // isShuttle, autoChained, sourceId.
    initialRoutes: { type: Array, default: () => [] },
    // Data del journey (YYYY-MM-DD) usata per costruire gli ISO datetime.
    date: { type: String, default: '' },
    // Spostamento in modifica: le sue corse già salvate non occupano la
    // disponibilità mentre si verifica. Vuoto in creazione.
    journeyId: { type: String, default: '' },
    // Edit-lock scattato (oltre le 15:00 del giorno prima): blocca tutti i
    // campi ma lascia attivo il cestino (eliminazione sempre consentita).
    locked: { type: Boolean, default: false },
    // Sedi del partner scelto: alimentano l'autocomplete di partenza e arrivo.
    headquarters: { type: Array, default: () => [] },
    // Partner dello spostamento: la tariffa delle corse è la sua.
    partnerId: { type: String, default: '' },
  },
  // Emesso quando viene eliminata una corsa PERSISTITA (sourceId): il parent
  // esegue il soft-delete (ELIMINATA) in attesa dell'approvazione admin.
  emits: ['route-removed'],

  setup(props, { emit }) {
    // Alert di sforamento a 3 scelte, gestito via el-dialog: la promise si
    // risolve SOLO quando l'utente sceglie (o chiude il dialog = 'cancel').
    const overlapVisible = ref(false);
    let overlapResolver = null;
    const resolveOverlap = () =>
      new Promise((resolve) => {
        overlapResolver = resolve;
        overlapVisible.value = true;
      });
    // Chiamata dai 3 pulsanti (e dalla chiusura del dialog).
    const chooseOverlap = (choice) => {
      overlapVisible.value = false;
      if (overlapResolver) {
        overlapResolver(choice);
        overlapResolver = null;
      }
    };
    // Chiusura via X/ESC senza scelta esplicita -> 'cancel' (default sicuro).
    const onOverlapClosed = () => {
      if (overlapResolver) {
        overlapResolver('cancel');
        overlapResolver = null;
      }
    };

    const legs = useJourneyLegs({
      initialRoutes: props.initialRoutes,
      date: toRef(props, 'date'),
      journeyId: toRef(props, 'journeyId'),
      giornata: toRef(props, 'giornata'),
      resolveOverlap,
      headquarters: toRef(props, 'headquarters'),
      partnerId: toRef(props, 'partnerId'),
    });

    const euro = (n) => `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    // La tariffa di un'auto, come si legge accanto al prezzo a mano.
    const testoTariffa = (v) => {
      switch (v.tariffaState) {
        case 'ok': return `${euro(v.tariffa)} + IVA`;
        case 'loading': return 'calcolo…';
        case 'missing': return 'nessuna fascia';
        case 'error': return 'non disponibile';
        default: return '—';
      }
    };

    // Popola anteprime + orari per le corse già presenti (edit mode).
    onMounted(() => legs.recomputeFrom(0));

    // Conferma esplicita prima di eliminare una corsa.
    const confirmRemove = async (index) => {
      try {
        await ElMessageBox.confirm(
          `Vuoi eliminare la Corsa ${index + 1}? L'operazione non è reversibile.`,
          'Elimina corsa',
          {
            confirmButtonText: 'Elimina',
            cancelButtonText: 'Annulla',
            type: 'warning',
          }
        );
        const removedSourceIds = legs.removeRoute(index);
        (removedSourceIds || []).forEach((sid) => emit('route-removed', sid));
      } catch (e) {
        // annullato dall'utente
      }
    };

    // Tutto ciò che viene ritornato dal setup è accessibile anche dal parent
    // via template ref (validate / buildRoutesDto / buildJourneyPayload).
    // Togliere un'auto in più: conferma, e se era già salvata il parent la
    // elimina come una corsa (soft-delete).
    const confermaTogliAuto = async (index, k) => {
      try {
        await ElMessageBox.confirm(`Vuoi togliere l'auto ${k + 2} dalla corsa ${index + 1}?`, 'Togli auto', {
          confirmButtonText: 'Togli',
          cancelButtonText: 'Annulla',
          type: 'warning',
        });
        const sid = legs.togliAuto(index, k);
        if (sid) emit('route-removed', sid);
      } catch (e) {
        // annullato dall'utente
      }
    };

    // «Sede B — via …» -> «Sede B», per le domande.
    const nomeArrivo = (route) => String(route?.endText || '').split(' — ')[0].trim() || 'destinazione';

    return {
      ...legs,
      euro,
      testoTariffa,
      nomeArrivo,
      confermaTogliAuto,
      confirmRemove,
      overlapVisible,
      chooseOverlap,
      onOverlapClosed,
    };
  },
};
</script>
